import { Suspense } from 'react';
import { lazyWithRetry } from './lazyWithRetry';
import { Navigate } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { PublicLayout } from '@layouts/PublicLayout';
import { config } from '@config/index';

const LandingPage = lazyWithRetry(() => import('@pages/LandingPage'));
const LoginPage = lazyWithRetry(() => import('@pages/auth/LoginPage'));
const SignupPage = lazyWithRetry(() => import('@pages/auth/SignupPage'));
const VerifyEmailPage = lazyWithRetry(() => import('@pages/auth/VerifyEmailPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('@pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('@pages/auth/ResetPasswordPage'));
const ForceChangePasswordPage = lazyWithRetry(() => import('@pages/auth/ForceChangePasswordPage'));
const NotFoundPage = lazyWithRetry(() => import('@pages/NotFoundPage'));
const UnderConstructionPage = lazyWithRetry(() => import('@pages/UnderConstructionPage'));
const FeaturesPage = lazyWithRetry(() => import('@pages/FeaturesPage'));
const PatientFeedbackPage = lazyWithRetry(() => import('@pages/features/PatientFeedbackPage'));
const PracticeAnalyticsPage = lazyWithRetry(() => import('@pages/features/PracticeAnalyticsPage'));
const PracticeBacklogPage = lazyWithRetry(() => import('@pages/features/PracticeBacklogPage'));
const PrivacyPolicyPage = lazyWithRetry(() => import('@pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazyWithRetry(() => import('@pages/legal/TermsOfServicePage'));
const SurveyCompletionPage = lazyWithRetry(() => import('@pages/SurveyCompletionPage'));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

export const publicRoutes: RouteObject[] = [
  {
    path: 's/:nanoid',
    element: <SuspenseWrapper><SurveyCompletionPage /></SuspenseWrapper>,
  },
  {
    element: <PublicLayout />,
    children: [
      {
        index: true,
        element: <SuspenseWrapper><LandingPage /></SuspenseWrapper>,
      },
      {
        path: 'features',
        element: <SuspenseWrapper><FeaturesPage /></SuspenseWrapper>,
      },
      {
        path: 'features/patient-feedback',
        element: <SuspenseWrapper><PatientFeedbackPage /></SuspenseWrapper>,
      },
      {
        path: 'features/practice-analytics',
        element: <SuspenseWrapper><PracticeAnalyticsPage /></SuspenseWrapper>,
      },
      {
        path: 'features/practice-backlog',
        element: <SuspenseWrapper><PracticeBacklogPage /></SuspenseWrapper>,
      },
      {
        path: 'privacy',
        element: <SuspenseWrapper><PrivacyPolicyPage /></SuspenseWrapper>,
      },
      {
        path: 'terms',
        element: <SuspenseWrapper><TermsOfServicePage /></SuspenseWrapper>,
      },
      {
        path: 'login',
        element: <SuspenseWrapper><LoginPage /></SuspenseWrapper>,
      },
      {
        path: 'signup',
        element: config.signupEnabled
          ? <SuspenseWrapper><SignupPage /></SuspenseWrapper>
          : <Navigate to="/login" replace />,
      },
      {
        path: 'verify-email',
        element: <SuspenseWrapper><VerifyEmailPage /></SuspenseWrapper>,
      },
      {
        path: 'forgot-password',
        element: <SuspenseWrapper><ForgotPasswordPage /></SuspenseWrapper>,
      },
      {
        path: 'reset-password',
        element: <SuspenseWrapper><ResetPasswordPage /></SuspenseWrapper>,
      },
      {
        path: 'force-change-password',
        element: <SuspenseWrapper><ForceChangePasswordPage /></SuspenseWrapper>,
      },
      {
        path: '404',
        element: <SuspenseWrapper><NotFoundPage /></SuspenseWrapper>,
      },
      {
        path: 'under-construction',
        element: <SuspenseWrapper><UnderConstructionPage /></SuspenseWrapper>,
      },
      {
        path: '*',
        element: <SuspenseWrapper><NotFoundPage /></SuspenseWrapper>,
      },
    ],
  },
];
